<script setup>
definePage({
  meta: {
    layout: 'default',
    action: 'read',
    subject: 'UserGuide',
  },
})

const sections = [
  {
    title: '🍽️ Apa itu Denah Meja?',
    icon: 'ri-table-2',
    color: 'primary',
    content: `
      <p class="mb-4">
        <strong>Denah Meja</strong> adalah halaman yang menampilkan semua meja di
        restoran/coffee shop Anda dalam bentuk kartu — lengkap dengan status
        terkini masing-masing meja. Anggap saja ini seperti "peta" tempat duduk
        yang selalu Anda lihat sebelum mengarahkan tamu ke meja yang kosong.
      </p>
      <p class="mb-4">
        Setiap meja punya salah satu dari 4 status berikut:
      </p>
      <VRow class="mb-4">
        <VCol cols="6" md="3">
          <VChip color="success" variant="tonal" class="mb-2">Tersedia</VChip>
          <p class="text-caption mb-0">Meja kosong, siap dipakai tamu baru</p>
        </VCol>
        <VCol cols="6" md="3">
          <VChip color="warning" variant="tonal" class="mb-2">Terisi</VChip>
          <p class="text-caption mb-0">Ada tamu sedang duduk/pesan di meja ini</p>
        </VCol>
        <VCol cols="6" md="3">
          <VChip color="info" variant="tonal" class="mb-2">Reservasi</VChip>
          <p class="text-caption mb-0">Meja sudah dipesan untuk nanti</p>
        </VCol>
        <VCol cols="6" md="3">
          <VChip color="secondary" variant="tonal" class="mb-2">Gabungan</VChip>
          <p class="text-caption mb-0">Meja ini digabung ke meja lain (lihat bagian Gabung Meja)</p>
        </VCol>
      </VRow>
      <VAlert type="success" variant="tonal">
        Menu ini bisa Anda buka lewat sidebar kiri: <strong>Ekstensi F&amp;B → Denah Meja</strong>.
      </VAlert>
    `,
  },
  {
    title: '➕ Menambah Meja Baru',
    icon: 'ri-add-circle-line',
    color: 'success',
    content: `
      <ol class="mb-4">
        <li class="mb-2">Buka menu <strong>Denah Meja</strong></li>
        <li class="mb-2">Klik tombol <strong>"+ Tambah Meja"</strong> di pojok kanan atas</li>
        <li class="mb-2">Isi form yang muncul</li>
        <li class="mb-0">Klik <strong>"Simpan"</strong></li>
      </ol>

      <table class="userguide-table mb-4">
        <thead>
          <tr><th>Field</th><th>Wajib?</th><th>Penjelasan</th><th>Contoh</th></tr>
        </thead>
        <tbody>
          <tr>
            <td><strong>Kode Meja</strong></td>
            <td>✅ Ya</td>
            <td>Kode singkat unik untuk identifikasi meja (tidak boleh sama dengan meja lain)</td>
            <td>T-01, A1, VIP-2</td>
          </tr>
          <tr>
            <td><strong>Nama Meja</strong></td>
            <td>❌ Tidak</td>
            <td>Nama tambahan supaya lebih mudah diingat</td>
            <td>Meja Sudut Jendela</td>
          </tr>
          <tr>
            <td><strong>Area</strong></td>
            <td>❌ Tidak</td>
            <td>Lokasi/zona meja, dipakai untuk filter di Denah Meja</td>
            <td>Indoor, Outdoor, Lantai 2</td>
          </tr>
          <tr>
            <td><strong>Kapasitas</strong></td>
            <td>✅ Ya</td>
            <td>Jumlah maksimal tamu yang muat di meja ini</td>
            <td>4</td>
          </tr>
          <tr>
            <td><strong>Aktif</strong></td>
            <td>✅ Ya (bawaan: aktif)</td>
            <td>Kalau dimatikan, meja tidak akan muncul untuk dipilih kasir</td>
            <td>Aktif</td>
          </tr>
        </tbody>
      </table>

      <VAlert type="info" variant="tonal">
        💡 Setiap meja otomatis mendapat <strong>QR Code Self-Order</strong> sendiri
        begitu dibuat — Anda tidak perlu setting apa pun untuk itu. Lihat bagian
        "QR Self-Order" di bawah.
      </VAlert>
    `,
  },
  {
    title: '🪑 Membuka Sesi Meja (Tamu Datang)',
    icon: 'ri-user-add-line',
    color: 'success',
    content: `
      <p class="mb-4">
        <strong>Sesi meja</strong> adalah "penanda" bahwa meja sedang dipakai oleh
        satu rombongan tamu, dari mereka duduk sampai selesai bayar. Anda harus
        membuka sesi dulu sebelum bisa mencatat pesanan untuk meja tersebut.
      </p>
      <ol class="mb-4">
        <li class="mb-2">Di halaman Denah Meja, klik kartu meja yang berstatus <strong>Tersedia</strong></li>
        <li class="mb-2">
          Muncul dialog <strong>"Buka Sesi"</strong> — isi <strong>Jumlah Tamu</strong>
          (opsional, contoh: 3)
        </li>
        <li class="mb-2">Klik <strong>"Buka Sesi"</strong></li>
        <li class="mb-0">
          Anda akan otomatis diarahkan ke halaman <strong>POS Kasir</strong> dengan
          meja tersebut sudah terpilih — tinggal input pesanan tamu
        </li>
      </ol>
      <VAlert type="warning" variant="tonal">
        ⚠️ Meja yang statusnya sudah <strong>Terisi</strong> tidak bisa dibuka sesi
        baru. Anda akan melihat pesan error kalau mencoba — ini untuk mencegah
        2 rombongan tamu tercampur di 1 meja yang sama.
      </VAlert>
    `,
  },
  {
    title: '🔗 Gabung Beberapa Meja',
    icon: 'ri-git-merge-line',
    color: 'info',
    content: `
      <p class="mb-4">
        Fitur ini dipakai kalau ada rombongan besar yang butuh lebih dari 1 meja,
        misalnya meja A1 dan A2 digabung jadi satu tagihan untuk 1 rombongan.
      </p>
      <ol class="mb-4">
        <li class="mb-2">Klik tombol <strong>"Gabung Meja"</strong> di pojok kanan atas Denah Meja</li>
        <li class="mb-2">Klik meja-meja yang ingin digabung (minimal 2 meja)</li>
        <li class="mb-2">Klik <strong>"Lanjut Gabungkan"</strong> yang muncul di bawah layar</li>
        <li class="mb-2">Pilih <strong>Meja Utama</strong> — meja ini yang akan jadi "induk" tagihan</li>
        <li class="mb-0">Klik <strong>"Gabungkan"</strong></li>
      </ol>
      <VAlert type="warning" variant="tonal" class="mb-3">
        ⚠️ <strong>Meja Utama wajib sudah punya sesi aktif</strong> (statusnya Terisi)
        sebelum digabung. Jadi urutan yang benar: buka sesi di 1 meja dulu →
        baru gabungkan meja-meja lain ke situ.
      </VAlert>
      <p class="mb-0 text-body-2">
        Setelah digabung, meja tambahan akan berubah status jadi
        <VChip color="secondary" size="x-small" variant="tonal">Gabungan</VChip>
        dan semua pesanan dicatat di bawah 1 sesi milik meja utama.
      </p>
    `,
  },
  {
    title: '↔️ Pindah Pesanan ke Meja Lain',
    icon: 'ri-arrow-left-right-line',
    color: 'secondary',
    content: `
      <p class="mb-4">
        Kadang tamu minta pindah tempat duduk setelah pesan. Fitur ini memindahkan
        <strong>pesanan yang sudah dibuat</strong> ke meja lain, tanpa perlu buat
        ulang dari awal.
      </p>
      <ol class="mb-4">
        <li class="mb-2">Setelah pesanan untuk meja tersebut selesai dibuat di kasir, akan muncul
          tombol <strong>"Pindah Meja"</strong> di layar struk</li>
        <li class="mb-2">Pilih meja tujuan (hanya menampilkan meja yang statusnya Tersedia)</li>
        <li class="mb-0">Klik <strong>"Pindahkan"</strong></li>
      </ol>
      <VAlert type="info" variant="tonal">
        Meja lama otomatis kembali berstatus <strong>Tersedia</strong> kalau tidak
        ada pesanan lain yang masih aktif di meja itu.
      </VAlert>
    `,
  },
  {
    title: '🔒 Menutup Sesi Meja',
    icon: 'ri-lock-line',
    color: 'error',
    content: `
      <p class="mb-4">
        Lakukan ini setelah tamu selesai makan dan <strong>semua tagihan sudah lunas</strong>
        — termasuk kalau tagihannya dipisah (lihat bagian Split Bill di bawah).
      </p>
      <ol class="mb-4">
        <li class="mb-2">Klik kartu meja yang berstatus Terisi/Gabungan</li>
        <li class="mb-2">Klik <strong>"Tutup Sesi"</strong> di dialog yang muncul</li>
        <li class="mb-0">Konfirmasi — meja kembali berstatus <strong>Tersedia</strong></li>
      </ol>
      <VAlert type="warning" variant="tonal">
        ⚠️ Sesi hanya bisa ditutup kalau semua order & split bill di meja itu
        sudah berstatus lunas. Kalau masih ada tagihan yang belum dibayar,
        sistem akan menolak dan menampilkan pesan error.
      </VAlert>
    `,
  },
  {
    title: '📱 QR Code Self-Order per Meja',
    icon: 'ri-qr-code-line',
    color: 'primary',
    content: `
      <p class="mb-4">
        Setiap meja punya <strong>QR Code unik</strong> yang bisa ditempel di meja.
        Kalau di-scan pelanggan pakai HP mereka, akan terbuka halaman pesan sendiri
        (self-order) khusus untuk meja itu.
      </p>
      <ol class="mb-4">
        <li class="mb-2">Di kartu meja, klik ikon <strong>QR (📱)</strong> di pojok kanan atas kartu</li>
        <li class="mb-2">Muncul dialog berisi gambar QR dan link lengkap self-order</li>
        <li class="mb-0">Gunakan tombol yang tersedia:</li>
      </ol>
      <table class="userguide-table mb-4">
        <thead>
          <tr><th>Tombol</th><th>Fungsi</th></tr>
        </thead>
        <tbody>
          <tr><td><strong>Copy Link</strong></td><td>Menyalin link self-order untuk dikirim manual (mis. lewat WhatsApp)</td></tr>
          <tr><td><strong>Buka</strong></td><td>Membuka halaman self-order di tab baru untuk dicoba/dicek</td></tr>
          <tr><td><strong>Download</strong></td><td>Menyimpan gambar QR ke perangkat Anda</td></tr>
          <tr><td><strong>Cetak</strong></td><td>Mencetak QR langsung (cocok untuk print sticker meja)</td></tr>
        </tbody>
      </table>
      <p class="mb-2 text-body-2">
        Ada juga ikon <strong>link (🔗)</strong> di kartu meja untuk langsung
        menyalin link tanpa membuka dialog — lebih cepat kalau Anda cuma butuh copy-paste.
      </p>
      <VAlert type="warning" variant="tonal">
        ⚠️ <strong>Belum tersedia saat ini:</strong> fitur "Generate ulang QR"
        (untuk menonaktifkan QR lama) dan "Buat Meja Massal" (bikin banyak meja
        sekaligus). Kedua fitur ini masih dalam pengembangan.
      </VAlert>
    `,
  },
  {
    title: '✂️ Split Bill (Pisah Tagihan)',
    icon: 'ri-scissors-cut-line',
    color: 'warning',
    content: `
      <p class="mb-4">
        Split Bill dipakai kalau satu meja isinya banyak tamu dan mereka mau
        bayar sendiri-sendiri, bukan 1 tagihan gabungan. Fitur ini ada di
        <strong>halaman kasir (POS)</strong>, muncul setelah pesanan untuk meja
        tersebut berhasil dibuat.
      </p>
      <ol class="mb-4">
        <li class="mb-2">Setelah checkout pesanan dine-in, di layar struk klik tombol
          <strong>"Split Bill"</strong></li>
        <li class="mb-2">Pilih salah satu mode pembagian:</li>
      </ol>
      <VRow class="mb-4">
        <VCol cols="12" md="6">
          <VCard variant="outlined">
            <VCardText>
              <strong>Per Item</strong>
              <p class="text-body-2 mb-0 mt-1">
                Anda tentukan sendiri item mana masuk ke "Bill 1", item mana
                masuk ke "Bill 2", dst. Cocok kalau tiap orang pesan beda menu
                dan mau bayar sesuai pesanannya sendiri.
              </p>
            </VCardText>
          </VCard>
        </VCol>
        <VCol cols="12" md="6">
          <VCard variant="outlined">
            <VCardText>
              <strong>Rata (Even)</strong>
              <p class="text-body-2 mb-0 mt-1">
                Total tagihan dibagi rata sesuai jumlah orang. Contoh: total
                Rp68.000 dibagi 3 orang → Rp22.666, Rp22.666, Rp22.668
                (sisa pembulatan masuk ke bill terakhir).
              </p>
            </VCardText>
          </VCard>
        </VCol>
      </VRow>
      <ol start="3" class="mb-4">
        <li class="mb-2">Klik <strong>"Buat Split"</strong> — daftar tagihan yang sudah
          dipecah akan muncul, masing-masing berstatus <em>Belum Bayar</em></li>
        <li class="mb-2">Klik <strong>"Bayar"</strong> di tagihan yang mau dilunasi,
          pilih metode pembayaran (Tunai, QRIS, Transfer, Kartu Debit/Kredit)</li>
        <li class="mb-0">Ulangi untuk setiap tagihan sampai semuanya berstatus <em>Lunas</em></li>
      </ol>
      <VAlert type="warning" variant="tonal">
        ⚠️ Untuk mode <strong>Per Item</strong>, semua item pesanan wajib
        dimasukkan ke salah satu bill sebelum tombol "Buat Split" bisa diklik —
        tidak boleh ada item yang "terlantar" tanpa bill.
      </VAlert>
    `,
  },
]
</script>

<template>
  <div>
    <RouterLink to="/userguide/fb-modern">
      <VBtn variant="text" prepend-icon="ri-arrow-left-line" class="mb-2">
        Kembali ke F&B Modern
      </VBtn>
    </RouterLink>

    <div class="d-flex align-center mb-4">
      <VAvatar color="primary" variant="tonal" size="64" rounded="lg" class="me-4">
        <VIcon icon="ri-table-2" size="36" />
      </VAvatar>
      <div>
        <h4 class="text-h4 font-weight-bold mb-1">Modul 13: Meja & Denah Meja</h4>
        <p class="text-body-1 text-medium-emphasis mb-0">
          Kelola meja, sesi, gabung/pindah meja, QR self-order, dan pisah tagihan
        </p>
      </div>
    </div>

    <VExpansionPanels variant="accordion" multiple>
      <VExpansionPanel v-for="(section, index) in sections" :key="index">
        <VExpansionPanelTitle>
          <VIcon :icon="section.icon" :color="section.color" class="me-3" />
          {{ section.title }}
        </VExpansionPanelTitle>
        <VExpansionPanelText>
          <div v-html="section.content" />
        </VExpansionPanelText>
      </VExpansionPanel>
    </VExpansionPanels>

    <!-- FAQ -->
    <h5 class="text-h5 font-weight-bold mt-6 mb-3">❓ Pertanyaan yang Sering Ditanyakan</h5>
    <VExpansionPanels variant="accordion">
      <VExpansionPanel>
        <VExpansionPanelTitle class="text-body-2 font-weight-medium">
          Apakah meja yang sedang terisi bisa dihapus?
        </VExpansionPanelTitle>
        <VExpansionPanelText>
          Tidak. Tombol hapus hanya aktif untuk meja berstatus <strong>Tersedia</strong>.
          Ini untuk mencegah meja yang sedang dipakai tamu tiba-tiba hilang dari sistem.
        </VExpansionPanelText>
      </VExpansionPanel>
      <VExpansionPanel>
        <VExpansionPanelTitle class="text-body-2 font-weight-medium">
          Kalau QR meja hilang/rusak, bagaimana cara cetak ulang?
        </VExpansionPanelTitle>
        <VExpansionPanelText>
          QR meja tidak berubah selama meja belum dihapus, jadi Anda bisa buka
          kembali dialog QR kapan saja lewat ikon QR di kartu meja, lalu klik
          "Cetak" atau "Download" untuk mendapat salinan baru.
        </VExpansionPanelText>
      </VExpansionPanel>
      <VExpansionPanel>
        <VExpansionPanelTitle class="text-body-2 font-weight-medium">
          Apa bedanya "Pindah Meja" dengan "Gabung Meja"?
        </VExpansionPanelTitle>
        <VExpansionPanelText>
          <strong>Pindah Meja</strong> memindahkan 1 pesanan dari satu meja ke
          meja lain (tamu pindah tempat duduk). <strong>Gabung Meja</strong>
          menyatukan 2 meja atau lebih jadi 1 sesi/tagihan (rombongan besar
          yang butuh lebih dari 1 meja).
        </VExpansionPanelText>
      </VExpansionPanel>
      <VExpansionPanel>
        <VExpansionPanelTitle class="text-body-2 font-weight-medium">
          Kalau salah bagi split bill, bisa dibatalkan?
        </VExpansionPanelTitle>
        <VExpansionPanelText>
          Split yang belum dibayar tetap terdaftar di sistem. Untuk perubahan
          split yang sudah dibuat, hubungi admin/pemilik toko Anda — pembatalan
          split lewat halaman kasir belum tersedia saat ini.
        </VExpansionPanelText>
      </VExpansionPanel>
    </VExpansionPanels>

    <!-- Related -->
    <h5 class="text-h5 font-weight-bold mt-6 mb-3">🔗 Halaman Terkait</h5>
    <VRow>
      <VCol
        v-for="link in [
          { title: 'Tipe Order & Biaya Layanan', sub: 'Atur biaya dine-in/takeaway/delivery', icon: 'ri-percent-line', color: 'success', to: { name: 'userguide-fb-modern-order-type' } },
          { title: 'POS Kasir', sub: 'Cara pakai meja & split bill saat transaksi', icon: 'ri-shopping-cart-2-line', color: 'primary', to: { name: 'userguide-operasional-pos-kasir' } },
        ]"
        :key="link.title"
        cols="12"
        md="6"
      >
        <RouterLink :to="link.to" class="text-decoration-none">
          <VCard variant="outlined" hover>
            <VCardText class="d-flex align-center gap-3 py-3">
              <VAvatar :color="link.color" variant="tonal" size="40" rounded>
                <VIcon :icon="link.icon" size="22" />
              </VAvatar>
              <div>
                <p class="text-body-2 font-weight-bold mb-0">{{ link.title }}</p>
                <p class="text-caption text-medium-emphasis mb-0">{{ link.sub }}</p>
              </div>
            </VCardText>
          </VCard>
        </RouterLink>
      </VCol>
    </VRow>
  </div>
</template>

<style scoped>
.userguide-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
}

.userguide-table th,
.userguide-table td {
  border: 1px solid rgba(var(--v-theme-on-surface), 0.12);
  padding: 8px 12px;
  text-align: left;
  vertical-align: top;
}

.userguide-table th {
  background: rgba(var(--v-theme-on-surface), 0.04);
  font-weight: 600;
}
</style>
